import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { TaskyAlert } from '../tasky';

/**
 * 生成中の「いま何をしていて、あとどれくらいか」を出す共通表示（UGC-02）。
 *
 * ## なぜ要るのか
 * 生成中であることは分かっても、何を処理しているか・どの程度待つかが分からなかった
 * （2026-08-22 大野FB / UGC-02）。書き出し画面の
 * 「テロップを配置しています」「シーンを接続しています」という段階表示は分かりやすいと
 * 評価されたので、その流儀を他の生成工程へ横展開する。
 *
 * ## 段階はクライアント側のタイマーで進める
 * backend の生成ジョブは細かい進捗を返さない（返せるのはクリップ生成の progress_step だけ）。
 * ここで出すのは**実際の内部状態ではなく、待ち時間の見通し**。
 * `ExportStep` の `useComposeStageMessage` と同じ割り切りで、実際の完了判定は
 * 呼び出し元のポーリングに委ねる。
 */

export interface GenerationProgressProps {
  /** 生成中かどうか。false の間は何も出さず、段階もリセットする */
  active: boolean;
  /** 処理段階のメッセージ（順に進む）。例: ['訴求軸を整理中', 'シーンを構成中'] */
  stages: string[];
  /** 「通常30秒ほどかかります」の目安。省略すると出さない */
  estimate?: string;
  /** 複数件を処理する工程の進捗。例: { done: 2, total: 5 } */
  counts?: { done: number; total: number };
  /** 段階を進める間隔（ミリ秒）。既定は書き出し画面と同じ 2600ms */
  intervalMs?: number;
  className?: string;
}

const DEFAULT_INTERVAL_MS = 2600;

/**
 * 段階メッセージを一定間隔で進める。実際の完了判定は呼び出し元のポーリングに委ねる（演出のみ）。
 */
export function useGenerationStage(
  active: boolean,
  stages: string[],
  intervalMs: number = DEFAULT_INTERVAL_MS
): { message: string; percent: number } {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!active || stages.length === 0) {
      setIndex(0);
      return undefined;
    }

    const timer = setInterval(() => {
      setIndex((i) => Math.min(stages.length - 1, i + 1));
    }, intervalMs);

    return () => clearInterval(timer);
  }, [active, stages.length, intervalMs]);

  // 最後の段階でも 100% にはしない。完了は呼び出し元のポーリングが決める
  const percent = stages.length === 0 ? 0 : Math.round(((index + 1) / stages.length) * 92);

  return { message: stages[Math.min(index, stages.length - 1)] ?? '', percent };
}

const GenerationProgress: React.FC<GenerationProgressProps> = ({
  active,
  stages,
  estimate,
  counts,
  intervalMs = DEFAULT_INTERVAL_MS,
  className,
}) => {
  const { message, percent } = useGenerationStage(active, stages, intervalMs);

  if (!active) return null;

  return (
    <TaskyAlert
      tone="info"
      icon={<Loader2 className="w-5 h-5 text-primary animate-spin" />}
      className={className}
    >
      <div className="w-full">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-sm">{message}</span>
          {counts && counts.total > 0 && (
            <span className="text-xs text-gray-500">
              {counts.done}/{counts.total}件 完了
            </span>
          )}
          {estimate && <span className="text-xs text-gray-500">{estimate}</span>}
        </div>

        <div className="mt-2 h-1.5 w-full rounded-full bg-gray-200">
          <div
            className="h-1.5 rounded-full bg-primary transition-all duration-500"
            style={{ width: `${counts && counts.total > 0 ? Math.round((counts.done / counts.total) * 100) : percent}%` }}
          />
        </div>

        {/* 画面を離れてよいかを明示する（UGC-02の受け入れ条件） */}
        <p className="mt-1.5 text-[11px] text-gray-500">この画面を離れても生成は続きます。</p>
      </div>
    </TaskyAlert>
  );
};

export default GenerationProgress;
